import { RealtimeChannel } from '@supabase/supabase-js';

import { RepoAsync } from './repoToggle';
import { v4 } from 'uuid';
import { IUser } from './IUser';
import { LobbyInvite, LobbyInviteCancel, LobbyInviteResponse,
  RestartRequest, RestartResponse, RestartCancel, PlayerForfeit, ReadyForNext,
  RollInput, RollResult, RoomHello, StartRound, StateHash, StateSync,
  TurnAck, MatchFoundMessage, PlayerReady, PathChunk, MatchCancelled,
  ObserverJoin, ObserverLeave, ObserverList, BoardMessage } from './MultiPlayerModel';
import { baseTiltX } from './PhysicsEngine';
import { TIMING } from './constants/timing';


export type PresenceMeta = {
  userId:string;
  userName:string;
  mode:'waiting'|'solo'|'in_match'|'searching';
  wins: number;
  losses: number;
  avgPPR: number;
  profile_image_url?: string;
};

export type LobbyHandlers = {
  showLobby?: (peers: PresenceMeta[]) => void;
  showInvite?: (fromName: string, accept: ()=> void, reject: ()=> void) => void;
  showInviteCancelled?: (fromName: string) => void;
  showInviteDeclined?: (fromName: string) => void;
  showRestartRequest?: (fromName: string, accept: ()=> void, reject: ()=> void) => void;
  showRestartAccepted?: (fromName: string) => void;
  showRestartDeclined?: (fromName: string) => void;
  showRestartCancelled?: () => void;
  onRestartAccepted?: () => void;
  showOpponentQuit?: (opponentName: string) => void;
  startMatch?: (matchId: string, player1: { userId: string, userName: string }, player2: { userId: string, userName: string }) => void;
  onOpponentRoll?: (rollInput: RollInput) => void;
  onOpponentRollResult?: (rollResult: RollResult) => void;
  onUserLeft?: (userName: string) => void;
  onTurnAck?: (ack: TurnAck) => void;
  onReadyForNext?: (ready: ReadyForNext) => void;
  onStateSync?: (sync: StateSync) => void;
  onPathChunk?: (chunk: PathChunk) => void;
  onBoardMessage?: (message: BoardMessage) => void;
  showMatchSearching?: (cancelSearch: () => void) => void;
  showAIPrompt?: () => void;
  onMatchFound?: (opponent: PresenceMeta, matchId: string) => void;
  showMatchIntroduction?: (currentPlayer: PresenceMeta, opponent: PresenceMeta, isStarting: boolean, matchId: string, onStartMatch: () => void, onCancel: () => void, titleText?: string, hideWL?: boolean) => void;
  showMatchCancelled?: (fromName: string, onClose?: () => void) => void;
  onObserverJoin?: (observer: { userId: string; userName: string }) => void;
  onObserverLeave?: (observer: { userId: string; userName: string }) => void;
};

export class LobbyManager {
  currentUser!: IUser;
  lobby!: RealtimeChannel;
  repo: RepoAsync;
  private synced = false;
  private currentPresenceKey: string;
  private handlers: LobbyHandlers = {};
  setHandlers(h: LobbyHandlers) { this.handlers = h; }
  buildLobbyList!: (users: PresenceMeta[])=> void;
  isGameHost = false;
  matchId!: string;
  private matchChannel: RealtimeChannel | null = null;
  private matchControls: any = null;
  private initialSyncPromise: Promise<void>;
  private resolveInitialSync!: () => void;
  private searchingForMatch = false;
  private playerReadyState: { [matchId: string]: { [userId: string]: boolean } } = {};
  private matches: { [matchId: string]: MatchFoundMessage } = {};
  private shownMatchIntros: Set<string> = new Set(); // Track which match intros have been shown to this player
  private refreshDebounceTimer: NodeJS.Timeout | null = null; // Debounce rapid presence sync refreshes

  constructor(repo: RepoAsync) {
    this.repo = repo;    
    // Use a neutral unique presence key for this client instance.
    // Avoid observer-specific prefixes so regular matches are unaffected.
    this.currentPresenceKey = v4();
    this.initialSyncPromise = new Promise(resolve => {
      this.resolveInitialSync = resolve;
    });
    this.init();
  }

  public get initialSyncComplete(): Promise<void> {
    return this.initialSyncPromise;
  }

  async userJoin(user: IUser) {
    this.currentUser = user;
    this.lobby.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        // Now that we are subscribed, track our presence.
        await this.lobby.track({
          userId: this.currentUser.id,
          userName: this.currentUser.userName,
          mode: 'waiting',
          wins: this.currentUser.wins,
          losses: this.currentUser.losses,
          avgPPR: this.currentUser.avgPPR,
          profile_image_url: this.currentUser.profile_image_url
        });
      }
    });
  }

  async init() {
    this.lobby = await this.repo.createLobby('lobby', this.currentPresenceKey);
    this.lobby.on('presence', { event: 'sync' }, async () => {
      if (!this.synced) {
        this.synced = true;
        this.resolveInitialSync(); // Resolve the promise on first sync
      }
      const state = this.lobby.presenceState() as Record<string, PresenceMeta[]>;
      const peers = Object.values(state).flat();
      const me = this.getSelf();

      // Handle matchmaking if we're searching
      if (me?.mode === "searching" && this.searchingForMatch) {
        this.checkForMatch(peers);
      }

      if (me?.mode === "waiting") {
        // Debounce rapid presence syncs to prevent excessive API calls
        if (this.refreshDebounceTimer) {
          clearTimeout(this.refreshDebounceTimer);
        }
        this.refreshDebounceTimer = setTimeout(async () => {
          // Skip refresh if lobby is hidden (e.g., tournament view is active)
          const lobbyEl = document.getElementById('skeech-lobby');
          if (lobbyEl && lobbyEl.style.display === 'none') {
            this.refreshDebounceTimer = null;
            return;
          }
          // Fetch fresh stats from DB for all online users
          const freshPeers = await this.refreshAndGetUserList();
          this.handlers.showLobby?.(freshPeers);
          this.refreshDebounceTimer = null;
        }, TIMING.LOBBY_REFRESH_DEBOUNCE);
      };
    })
    .on('broadcast', { event: 'lobby' }, async ({ payload }) => {
      const msg = payload as any;
      if (msg.kind === 'player_ready' && msg.to.userId === this.currentUser.id) {
        if (!this.playerReadyState[msg.matchId]) {
          this.playerReadyState[msg.matchId] = {};
        }
        this.playerReadyState[msg.matchId][msg.from.userId] = true;

        if (this.playerReadyState[msg.matchId][this.currentUser.id]) {
            const matchInfo = this.matches[msg.matchId];
            if (matchInfo) {
                const isStarting = matchInfo.startingPlayer === this.currentUser.id;
                const opponent = matchInfo.from.userId === this.currentUser.id ? matchInfo.to : matchInfo.from;

                if (isStarting) {
                    this.isGameHost = false;
                    this.handlers.startMatch?.(msg.matchId,
                        { userId: this.currentUser.id, userName: this.currentUser.userName },
                        { userId: opponent.userId, userName: opponent.userName }
                    );
                } else {
                    this.isGameHost = true;
                    this.handlers.startMatch?.(msg.matchId,
                        { userId: opponent.userId, userName: opponent.userName },
                        { userId: this.currentUser.id, userName: this.currentUser.userName }
                    );
                }
            }
        }
      } else if ((msg as MatchFoundMessage).kind === 'match_found' && this.searchingForMatch && !msg.isTournament && msg.to.userId === this.currentUser.id) {
        // Auto-open only in "Find a match" flow for the receiving player (not for tournament matches)
        if (this.matchControls) {
          return; // already in a match; ignore
        }
        const opponent = msg.from; // we are the receiver in search flow
        this.matches[msg.matchId] = msg; // store regardless
        const isStarting = msg.startingPlayer === this.currentUser.id;

        // Calculate fresh W-L for both players
        let currentWL = { wins: 0, losses: 0, gamesPlayed: 0 };
        let opponentWL = { wins: 0, losses: 0, gamesPlayed: 0 };
        try {
          if (this.repo.calculateWinLoss) {
            currentWL = await this.repo.calculateWinLoss(this.currentUser.id, { excludeTournaments: true, excludeAI: true });
            opponentWL = await this.repo.calculateWinLoss(opponent.userId, { excludeTournaments: true, excludeAI: true });
          }
        } catch (e) {
          console.error('Failed to calculate W-L for match intro', e);
        }

        const currentPlayer: PresenceMeta = {
          userId: this.currentUser.id,
          userName: this.currentUser.userName,
          mode: 'waiting', wins: currentWL.wins, losses: currentWL.losses, avgPPR: this.currentUser.avgPPR,
          profile_image_url: this.currentUser.profile_image_url
        };
        const opponentWithWL = { ...opponent, wins: opponentWL.wins, losses: opponentWL.losses };
        this.handlers.onMatchFound?.(opponentWithWL as any, msg.matchId);
        // Mark that we've shown the match intro to this player
        this.shownMatchIntros.add(msg.matchId);
        this.handlers.showMatchIntroduction?.(currentPlayer, opponentWithWL as any, isStarting, msg.matchId, () => {
          if (!this.playerReadyState[msg.matchId]) this.playerReadyState[msg.matchId] = {};
          this.playerReadyState[msg.matchId][this.currentUser.id] = true;
          this.lobby.send({ type: 'broadcast', event: 'lobby', payload: <PlayerReady>{ kind: 'player_ready', matchId: msg.matchId, from: { userId: this.currentUser.id, userName: this.currentUser.userName }, to: opponent as any } });
          if (this.playerReadyState[msg.matchId][(opponent as any).userId]) {
            if (isStarting) {
              this.isGameHost = false;
              this.handlers.startMatch?.(msg.matchId, { userId: this.currentUser.id, userName: this.currentUser.userName }, { userId: (opponent as any).userId, userName: (opponent as any).userName });
            } else {
              this.isGameHost = true;
              this.handlers.startMatch?.(msg.matchId, { userId: (opponent as any).userId, userName: (opponent as any).userName }, { userId: this.currentUser.id, userName: this.currentUser.userName });
            }
          }
        }, () => {
          // User clicked cancel
          this.lobby.send({ type: 'broadcast', event: 'lobby', payload: <MatchCancelled>{ kind: 'match_cancelled', matchId: msg.matchId, from: { userId: this.currentUser.id, userName: this.currentUser.userName }, to: opponent as any, isTournament: false } });
          delete this.playerReadyState[msg.matchId];
          delete this.matches[msg.matchId];
          this.shownMatchIntros.delete(msg.matchId);
          // Stop searching and return to lobby
          this.cancelMatchSearch();
        });

      } else if (msg.kind === 'invite' && msg.to.userId === this.currentUser.id) {
        this.handlers.showInvite?.(
          msg.from.userName,
          () => {
            this.lobby.send({
              type: 'broadcast',
              event: 'lobby',
              payload: <LobbyInviteResponse>{
                kind: 'invite_response',
                matchId: msg.matchId,
                to: { userId: msg.from.userId, userName: msg.from.userName },
                from: { userId: msg.to.userId, userName: msg.to.userName },
                accept: true
              }
            });
            // Person who accepts the invite is NOT the host (the sender is the host)
            this.isGameHost = false;
            this.handlers.startMatch?.(msg.matchId, { userId: this.currentUser.id, userName: this.currentUser.userName }, { userId: msg.from.userId, userName: msg.from.userName });
          },
          () => {
            this.lobby.send({
              type: 'broadcast',
              event: 'lobby',
              payload: <LobbyInviteResponse>{
                kind: 'invite_response',
                matchId: msg.matchId,
                to: { userId: msg.from.userId, userName: msg.from.userName },
                from: { userId: msg.to.userId, userName: msg.to.userName },
                accept: false
              }
            });
          });
      } else if (msg.kind === 'invite_response' && msg.to.userId === this.currentUser.id) {
        if (msg.accept) {
          this.isGameHost = true;
          this.handlers.startMatch?.(msg.matchId, { userId: msg.from.userId, userName: msg.from.userName }, { userId: msg.to.userId, userName: msg.to.userName });
        } else {
          this.handlers.showInviteDeclined?.(this.currentUser.userName);
        }
      } else if (msg.kind === 'invite_cancel' && msg.to.userId === this.currentUser.id) {
        this.handlers.showInviteCancelled?.(msg.from.userName);
      } else if (msg.kind === 'restart_request' && msg.to.userId === this.currentUser.id) {
        this.handlers.showRestartRequest?.(
          msg.from.userName,
          async () => {
            this.lobby.send({
              type: 'broadcast',
              event: 'lobby',
              payload: <RestartResponse>{
                kind: 'restart_response',
                matchId: msg.matchId,
                to: { userId: msg.from.userId, userName: msg.from.userName },
                from: { userId: this.currentUser.id, userName: this.currentUser.userName },
                accept: true
              }
            });
            this.handlers.onRestartAccepted?.();
          },
          () => {
            this.lobby.send({
              type: 'broadcast',
              event: 'lobby',
              payload: <RestartResponse>{
                kind: 'restart_response',
                matchId: msg.matchId,
                to: { userId: msg.from.userId, userName: msg.from.userName },
                from: { userId: this.currentUser.id, userName: this.currentUser.userName },
                accept: false
              }
            });
          }
                );
              } else if (msg.kind === 'restart_response' && msg.to.userId === this.currentUser.id) {        if (msg.accept) {
          this.handlers.showRestartAccepted?.(msg.from.userName);
        } else {
          this.handlers.showRestartDeclined?.(msg.from.userName);
        }
      } else if (msg.kind === 'restart_cancel' && msg.to.userId === this.currentUser.id) {
        this.handlers.showRestartCancelled?.();
      } else if (msg.kind === 'player_forfeit' && msg.to.userId === this.currentUser.id) {
        this.handlers.showOpponentQuit?.(msg.from.userName);
      } else if (msg.kind === 'match_cancelled' && msg.to.userId === this.currentUser.id) {
        // Only show cancellation notification if we've actually seen the match intro
        // Skip notification entirely for tournament games
        if (this.shownMatchIntros.has(msg.matchId) && !msg.isTournament) {
          this.handlers.showMatchCancelled?.(msg.from.userName);
        }
        // Clean up match state
        delete this.playerReadyState[msg.matchId];
        delete this.matches[msg.matchId];
        this.shownMatchIntros.delete(msg.matchId);
      } else if (msg.kind === 'match_found' && msg.to.userId === this.currentUser.id) {
        // Store tournament matches and wait for user to click bracket match to open intro
        // Only cancel search if this is a tournament match (don't interfere with regular matchmaking)
        if (msg.isTournament) {
          this.matches[msg.matchId] = msg as any;
        }
      }
    });
  }

  async startMatch(matchId: string) {
    const shortId = matchId.substring(0, 4);
    const logPrefix = `[${this.currentUser.userName}][${shortId}]`;

    // If we are already in this match, just return the existing controls
    if (this.matchId === matchId && this.matchChannel && this.matchControls) {
      console.log(`${logPrefix} startMatch: Already in match, returning existing controls.`);
      return this.matchControls;
    }
    console.log(`${logPrefix} startMatch: Joining match for the first time.`);

    // If we were in a different match, clean it up first.
    if (this.matchChannel) {
      console.log(`${logPrefix} startMatch: Unsubscribing from old match channel ${this.matchId?.substring(0, 4)}.`);
      await this.matchChannel.unsubscribe();
    }

    this.matchId = matchId;
    
    const room = await this.repo.createLobby(`match:${matchId}`, this.currentPresenceKey);
    this.matchChannel = room;

    room.on('presence',
      { event: 'leave' },
      ({ key, leftPresences }: { key: string, leftPresences: PresenceMeta[] }) => {
        const leftUser = leftPresences?.[0] as PresenceMeta;
        console.log(`${logPrefix} PRESENCE LEAVE EVENT:`, leftUser);
        if (leftUser && leftUser.userId !== this.currentUser.id) {
          this.handlers.onUserLeft?.(leftUser.userName);
        }
      }).on('broadcast', { event: 'room' }, ({ payload }) => {
        const m = payload as RoomHello | StartRound | RollInput | RollResult | StateHash | TurnAck | ReadyForNext | StateSync | PathChunk | BoardMessage | ObserverJoin | ObserverLeave | ObserverList;
        if (m.kind === 'start_round') {
        } else if (m.kind === 'roll_input') {
          this.handlers.onOpponentRoll?.(m);
        } else if (m.kind === 'turn_ack') {
          this.handlers.onTurnAck?.(m);
        } else if (m.kind === 'roll_result') {
          this.handlers.onOpponentRollResult?.(m);
        } else if (m.kind === 'ready_for_next') {
          this.handlers.onReadyForNext?.(m);
        } else if (m.kind === 'state_sync') {
          this.handlers.onStateSync?.(m);
        } else if (m.kind === 'path_chunk') {
          try {
            // console.log(`${logPrefix} recv path_chunk: round=${(m as any).round} ballIndex=${(m as any).ballIndex} snapshots=${(m as any).snapshots?.length ?? 0} complete=${(m as any).isComplete}`);
          } catch {}
          this.handlers.onPathChunk?.(m);
        } else if (m.kind === 'board_message') {
          this.handlers.onBoardMessage?.(m);
        } else if (m.kind === 'observer_join') {
          console.log(`${logPrefix} Observer joined:`, m.from.userName);
          this.handlers.onObserverJoin?.(m.from);
        } else if (m.kind === 'observer_leave') {
          console.log(`${logPrefix} Observer left:`, m.from.userName);
          this.handlers.onObserverLeave?.(m.from);
        }
      }).subscribe(async status => {
        if (status === 'SUBSCRIBED') {
          console.log(`${logPrefix} SUBSCRIBED to match channel.`);
          await room.track({
            userId: this.currentUser.id,
            userName: this.currentUser.userName,
            mode: 'in_match',
            wins: this.currentUser.wins,
            losses: this.currentUser.losses,
            avgPPR: this.currentUser.avgPPR
          });
          room.send({
            type: 'broadcast', event: 'room',
            payload: <RoomHello>{ kind: 'hello', matchId, userId: this.currentUser.id, roundSeed: Math.floor(Math.random() * 1e9) }
          });
          if (this.isGameHost) {
            const tiltX = baseTiltX + (Math.random() * 2.4 - 1.2);
            room.send({
              type: 'broadcast', event: 'room',
              payload: <StartRound>{ kind: 'start_round', round: 1, tiltX, seed: Math.floor(Math.random() * 1e9) }
            });
          }
        }
      });

    this.matchControls = {
      sendRollInput: (msg: RollInput) => room.send({ type: 'broadcast', event: 'room', payload: msg }),
      sendRollResult: (msg: RollResult) => {
        room.send({ type: 'broadcast', event: 'room', payload: msg });        
      },
      sendStartRound: (msg: StartRound) => room.send({ type: 'broadcast', event: 'room', payload: msg }),
      sendTurnAck: (msg: TurnAck) => room.send({ type: 'broadcast', event: 'room', payload: msg }),
      sendReadyForNext: (msg: ReadyForNext) => room.send({ type: 'broadcast', event: 'room', payload: msg }),
      sendStateSync: (msg: StateSync) => room.send({ type: 'broadcast', event: 'room', payload: msg }),
      sendPathChunk: (msg: PathChunk) => room.send({ type: 'broadcast', event: 'room', payload: msg }),
      sendMatchMessage: (payload: any) => room.send({ type: 'broadcast', event: 'room', payload: payload }),
      close: async () => {
        console.log(`${logPrefix} Closing connection to match.`);
        try { await this.matchChannel?.untrack(); } catch { }
        await this.matchChannel?.unsubscribe();
        this.matchChannel = null;
        this.matchControls = null;

        // Cleanup match state
        delete this.playerReadyState[this.matchId];
        delete this.matches[this.matchId];

        this.matchId = '';
      },
      matchId: this.matchId
    };
    return this.matchControls;
  }

  /**
   * Join a match channel as an observer (spectator)
   * Observers receive broadcasts but cannot send game-affecting messages
   * Returns minimal match controls for observers (no game-affecting sends)
   */
  async joinMatchAsObserver(matchId: string): Promise<any> {
    const logPrefix = '[LobbyManager]';
    console.log(`${logPrefix} joinMatchAsObserver: Joining match ${matchId.substring(0, 8)} as observer`);

    // If we were in a different match, clean it up first
    if (this.matchChannel) {
      console.log(`${logPrefix} joinMatchAsObserver: Unsubscribing from old match channel`);
      await this.matchChannel.unsubscribe();
    }

    this.matchId = matchId;

    const room = await this.repo.createLobby(`match:${matchId}`, this.currentPresenceKey);
    this.matchChannel = room;

    room.on('presence',
      { event: 'leave' },
      ({ key, leftPresences }: { key: string, leftPresences: PresenceMeta[] }) => {
        const leftUser = leftPresences?.[0] as PresenceMeta;
        console.log(`${logPrefix} [Observer] PRESENCE LEAVE EVENT:`, leftUser);
        if (leftUser && leftUser.userId !== this.currentUser.id) {
          this.handlers.onUserLeft?.(leftUser.userName);
        }
      }).on('broadcast', { event: 'room' }, ({ payload }) => {
        const m = payload as RoomHello | StartRound | RollInput | RollResult | StateHash | TurnAck | ReadyForNext | StateSync | PathChunk | BoardMessage | ObserverJoin | ObserverLeave | ObserverList;

        // Handle observer-specific messages
        if (m.kind === 'observer_join') {
          console.log(`${logPrefix} [Observer] Observer joined:`, m.from.userName);
          this.handlers.onObserverJoin?.(m.from);
        } else if (m.kind === 'observer_leave') {
          console.log(`${logPrefix} [Observer] Observer left:`, m.from.userName);
          this.handlers.onObserverLeave?.(m.from);
        } else if (m.kind === 'observer_list') {
          console.log(`${logPrefix} [Observer] Received observer list:`, m.observers);
          // Update local observer list (handled by game instance)
        }
        // Handle game messages (same as normal player)
        else if (m.kind === 'roll_input') {
          this.handlers.onOpponentRoll?.(m);
        } else if (m.kind === 'turn_ack') {
          this.handlers.onTurnAck?.(m);
        } else if (m.kind === 'roll_result') {
          this.handlers.onOpponentRollResult?.(m);
        } else if (m.kind === 'ready_for_next') {
          this.handlers.onReadyForNext?.(m);
        } else if (m.kind === 'state_sync') {
          this.handlers.onStateSync?.(m);
        } else if (m.kind === 'path_chunk') {
          this.handlers.onPathChunk?.(m);
        } else if (m.kind === 'board_message') {
          this.handlers.onBoardMessage?.(m);
        }
      }).subscribe(async status => {
        if (status === 'SUBSCRIBED') {
          console.log(`${logPrefix} [Observer] SUBSCRIBED to match channel as observer`);

          // Send ObserverJoin message (not tracking presence as player)
          room.send({
            type: 'broadcast', event: 'room',
            payload: <ObserverJoin>{
              kind: 'observer_join',
              matchId,
              from: { userId: this.currentUser.id, userName: this.currentUser.userName }
            }
          });
        }
      });

    // Store minimal controls for observers (no game-affecting sends)
    this.matchControls = {
      sendMatchMessage: (payload: any) => room.send({ type: 'broadcast', event: 'room', payload: payload }),
      close: async () => {
        console.log(`${logPrefix} [Observer] Leaving match as observer`);

        // Send ObserverLeave message
        room.send({
          type: 'broadcast', event: 'room',
          payload: <ObserverLeave>{
            kind: 'observer_leave',
            matchId: this.matchId,
            from: { userId: this.currentUser.id, userName: this.currentUser.userName }
          }
        });

        await this.matchChannel?.unsubscribe();
        this.matchChannel = null;
        this.matchControls = null;
        this.matchId = '';
      },
      matchId: this.matchId
    };
    return this.matchControls;
  }

  /**
   * Leave match channel as an observer
   */
  async leaveMatchAsObserver(matchId: string): Promise<void> {
    if (this.matchControls && this.matchId === matchId) {
      await this.matchControls.close();
    }
  }

  async updateMode(mode: PresenceMeta['mode']) {
    if (!this.currentUser) return;
    // This is an upsert. If presence exists, it's updated. If not, it's created.
    await this.lobby.track({
      userId: this.currentUser.id,
      userName: this.currentUser.userName,
      mode: mode,
      wins: this.currentUser.wins ?? 0,
      losses: this.currentUser.losses ?? 0,
      avgPPR: this.currentUser.avgPPR,
      profile_image_url: this.currentUser.profile_image_url
    });
  }

  async refreshPresence() {
    // Update presence with current user data (useful when user data changes)
    if (!this.currentUser) return;
    const currentState = this.getSelf();

    // If we don't have a state, we can't refresh it.
    // This can happen if the user is not in the lobby (e.g. in a match).
    // We can't just create a new state because we don't know the mode.
    // So, we do nothing. When the user returns to the lobby, a new presence will be created.
    if (!currentState) return;

    await this.lobby.track({
      ...currentState,
      userName: this.currentUser.userName,
      wins: this.currentUser.wins ?? 0,
      losses: this.currentUser.losses ?? 0,
      avgPPR: this.currentUser.avgPPR,
      profile_image_url: this.currentUser.profile_image_url
    });
  }

  // /** Call this when leaving lobby or on logout */
  async leaveLobby() {
    try { await this.lobby.untrack(); } catch {}
    try { await this.lobby.unsubscribe(); } catch {}
  }

  // /** Fire an invite through the lobby broadcast */
  async sendInvite(payload: any) {
    await this.lobby.send({ type: 'broadcast', event: 'lobby', payload });
  }

  // /** Send a restart request to opponent */
  async sendRestartRequest(matchId: string, opponent: { userId: string, userName: string }) {
    const payload: RestartRequest = {
      kind: 'restart_request',
      matchId,
      from: { userId: this.currentUser.id, userName: this.currentUser.userName },
      to: opponent
    };
    await this.lobby.send({ type: 'broadcast', event: 'lobby', payload });
  }

  async sendRestartCancel(matchId: string, opponent: { userId: string, userName: string }) {
    const payload: RestartCancel = {
      kind: 'restart_cancel',
      matchId,
      from: { userId: this.currentUser.id, userName: this.currentUser.userName },
      to: opponent
    };
    await this.lobby.send({ type: 'broadcast', event: 'lobby', payload });
  }

  // /** Send forfeit notification to opponent */
  async sendPlayerForfeit(matchId: string, opponent: { userId: string, userName: string }) {
    const payload: PlayerForfeit = {
      kind: 'player_forfeit',
      matchId,
      from: { userId: this.currentUser.id, userName: this.currentUser.userName },
      to: opponent
    };
    await this.lobby.send({ type: 'broadcast', event: 'lobby', payload });
  }

  // /** Read current presence list (valid after first sync) */
  getList(): PresenceMeta[] {
    const raw = this.lobby.presenceState() as Record<string, PresenceMeta[]>;
    return Object.values(raw).flat();
  }

  // /** Manually refresh and return user list with fresh DB data */
  async refreshAndGetUserList(): Promise<PresenceMeta[]> {
    const currentList = this.getList();
    const updatedList: PresenceMeta[] = [];

    // Fetch fresh data for each user from the database
    for (const user of currentList) {
      const freshUser = await this.repo.getUserById(user.userId);
      if (freshUser) {
        // OPTIMIZED: Fetch games once and calculate both PPR and W-L from the same data
        let freshPPR = 0; // Initialize to 0, will be calculated from game roll histories
        let freshWL = { wins: 0, losses: 0, gamesPlayed: 0 };

        try {
          // Fetch all games for this user ONCE
          const allGames = await this.repo.listGamesByUser(user.userId);

          // Calculate PPR (excludes tournaments and solo games)
          const rolls: number[] = [];
          for (const g of allGames as any[]) {
            if (g.isSoloGame) continue; // exclude solo
            if (g.tournament_id) continue; // exclude tournament
            const h1 = (g.p1UserId === user.userId) ? g.p1_roll_history : null;
            const h2 = (g.p2UserId === user.userId) ? g.p2_roll_history : null;
            const hist = (h1 && Array.isArray(h1)) ? h1 : ((h2 && Array.isArray(h2)) ? h2 : []);
            for (const v of hist) {
              const n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
              if (Number.isFinite(n)) rolls.push(n);
            }
          }
          if (rolls.length > 0) {
            const sum = rolls.reduce((a, b) => a + b, 0);
            freshPPR = +(sum / rolls.length).toFixed(3);
          }
          // If rolls.length === 0, freshPPR remains 0 (no games played)

          // Calculate W-L (excludes tournaments and AI games)
          let wins = 0;
          let losses = 0;
          let gamesPlayed = 0;
          for (const game of allGames as any[]) {
            if (game.status !== 'completed' && game.status !== 'forfeited') continue;
            if (game.isSoloGame) continue;
            if (game.tournament_id) continue; // exclude tournaments
            if (game.p1UserId === 'ai_robo_roller' || game.p2UserId === 'ai_robo_roller') continue; // exclude AI

            gamesPlayed++;
            if (game.winnerId === user.userId) {
              wins++;
            } else if (game.winnerId) {
              losses++;
            }
          }
          freshWL = { wins, losses, gamesPlayed };

        } catch (e) {
          console.error('Failed to calculate stats for', user.userName, e);
        }

        const userObject = {
          ...user,
          userName: freshUser.userName,
          wins: freshWL.wins,
          losses: freshWL.losses,
          avgPPR: freshPPR,
          profile_image_url: freshUser.profile_image_url
        };
        updatedList.push(userObject);
      } else {
        // If fetch fails, keep the cached presence data
        updatedList.push(user);
      }
    }

    return updatedList;
  }

  // /** Convenience: find our own meta by key */
  private getSelf(): PresenceMeta | null {
    const raw = this.lobby.presenceState() as Record<string, PresenceMeta[]>;
    const arr = raw[this.currentPresenceKey];
    return (arr && arr[0]) || null;
  }

  private getSelfFromRoom(room: RealtimeChannel): PresenceMeta | null {
    const raw = room.presenceState() as Record<string, PresenceMeta[]>;
    const arr = raw[this.currentPresenceKey];
    return (arr && arr[0]) || null;
  }

  public getUsersInMatch(): PresenceMeta[] {
    if (!this.matchChannel) {
      return [];
    }
    const raw = this.matchChannel.presenceState() as Record<string, PresenceMeta[]>;
    return Object.values(raw).flat();
  }

  // ** Matchmaking functionality **
  async startMatchSearch() {
    this.searchingForMatch = true;
    await this.updateMode('searching');

    // Show searching dialog to user
    this.handlers.showMatchSearching?.(() => this.cancelMatchSearch());

    setTimeout(() => {
      if (this.searchingForMatch) {
        this.handlers.showAIPrompt?.();
      }
    }, 3000);

    // Check if there are already users searching
    const peers = this.getList();
    this.checkForMatch(peers);
  }

  async cancelMatchSearch() {
    this.searchingForMatch = false;
    await this.updateMode('waiting');
  }

  private async checkForMatch(peers: PresenceMeta[]) {
    if (!this.searchingForMatch) return;

    // Find other users who are searching (excluding ourselves)
    const searchingUsers = peers.filter(user =>
      user.mode === 'searching' &&
      user.userId !== this.currentUser.id
    );

    if (searchingUsers.length > 0) {
      // Match found! Pick the first available user
      const opponent = searchingUsers[0];

      // Only the user with the lexicographically smaller userId creates and sends the match
      // This ensures only one match is created for the pair
      const shouldCreateMatch = this.currentUser.id < opponent.userId;

      if (!shouldCreateMatch) {
        // This user should wait for the other player to send the match message
        return;
      }

      const matchId = v4();
      this.searchingForMatch = false;

      // Randomly determine starting player
      const startingPlayer = Math.random() < 0.5 ? this.currentUser.id : opponent.userId;

      // Calculate fresh W-L for both players
      let currentWL = { wins: 0, losses: 0, gamesPlayed: 0 };
      let opponentWL = { wins: 0, losses: 0, gamesPlayed: 0 };
      try {
        if (this.repo.calculateWinLoss) {
          currentWL = await this.repo.calculateWinLoss(this.currentUser.id, { excludeTournaments: true, excludeAI: true });
          opponentWL = await this.repo.calculateWinLoss(opponent.userId, { excludeTournaments: true, excludeAI: true });
        }
      } catch (e) {
        console.error('[LobbyManager.checkForMatch] Failed to calculate W-L for match intro', e);
      }

      // Send match found message to opponent
      const matchFoundMsg: MatchFoundMessage = {
        kind: 'match_found',
        matchId,
        from: {
          userId: this.currentUser.id,
          userName: this.currentUser.userName,
          wins: currentWL.wins,
          losses: currentWL.losses,
          avgPPR: this.currentUser.avgPPR
        },
        to: {
          userId: opponent.userId,
          userName: opponent.userName
        },
        startingPlayer
        // isTournament is undefined for regular matchmaking
      };

      // Send the match message
      this.lobby.send({
        type: 'broadcast',
        event: 'lobby',
        payload: matchFoundMsg
      });

      // Show introduction for this player
      const currentPlayer: PresenceMeta = {
        userId: this.currentUser.id,
        userName: this.currentUser.userName,
        mode: 'waiting',
        wins: currentWL.wins,
        losses: currentWL.losses,
        avgPPR: this.currentUser.avgPPR
      };

      const isStarting = startingPlayer === this.currentUser.id;

      // Create opponent object with calculated W-L
      const opponentWithWL = { ...opponent, wins: opponentWL.wins, losses: opponentWL.losses };

      this.handlers.onMatchFound?.(opponentWithWL as any, matchId);
      this.matches[matchId] = matchFoundMsg;

      // Show introduction dialog
      this.handlers.showMatchIntroduction?.(currentPlayer, opponentWithWL as any, isStarting, matchId, () => {
        // User clicked "Start Game"
        if (!this.playerReadyState[matchId]) {
            this.playerReadyState[matchId] = {};
        }
        this.playerReadyState[matchId][this.currentUser.id] = true;

        this.lobby.send({
            type: 'broadcast',
            event: 'lobby',
            payload: <PlayerReady>{
                kind: 'player_ready',
                matchId: matchId,
                from: { userId: this.currentUser.id, userName: this.currentUser.userName },
                to: { userId: opponent.userId, userName: opponent.userName },
            }
        });

        // Check if opponent is also ready
        if (this.playerReadyState[matchId][opponent.userId]) {
            if (isStarting) {
                this.isGameHost = false;
                this.handlers.startMatch?.(matchId,
                    { userId: this.currentUser.id, userName: this.currentUser.userName },
                    { userId: opponent.userId, userName: opponent.userName }
                );
            } else {
                this.isGameHost = true;
                this.handlers.startMatch?.(matchId,
                    { userId: opponent.userId, userName: opponent.userName },
                    { userId: this.currentUser.id, userName: this.currentUser.userName }
                );
            }
        }
      }, () => {
        // User clicked cancel
        this.lobby.send({ type: 'broadcast', event: 'lobby', payload: <MatchCancelled>{ kind: 'match_cancelled', matchId: matchId, from: { userId: this.currentUser.id, userName: this.currentUser.userName }, to: { userId: opponent.userId, userName: opponent.userName } } });
        delete this.playerReadyState[matchId];
        delete this.matches[matchId];
        // Stop searching and return to lobby
        this.cancelMatchSearch();
      });
    }
  }

  // Start a direct match with a known opponent (used for tournaments)
  async startDirectMatch(opponent: PresenceMeta, opts?: { matchId?: string; startingPlayerId?: string; titleText?: string; isTournament?: boolean; tournamentId?: string }) {
    const matchId = opts?.matchId || v4();
    const startingPlayer = opts?.startingPlayerId || (Math.random() < 0.5 ? this.currentUser.id : opponent.userId);

    // Determine tournament type and load appropriate W-L records
    let hideWL = false; // Default: show W-L
    let currentWL = { wins: 0, losses: 0, gamesPlayed: 0 };
    let opponentWL = { wins: 0, losses: 0, gamesPlayed: 0 };

    if (opts?.tournamentId && (this.repo as any).sb?.getTournamentById) {
      try {
        const tournament = await (this.repo as any).sb.getTournamentById(opts.tournamentId);
        const tournamentType = tournament?.tournament_type as 'bracket' | 'round_robin' | undefined;

        if (tournamentType === 'round_robin' && tournament.round_robin_data?.standings) {
          // For round robin: load tournament-specific W-L from standings
          const standings = tournament.round_robin_data.standings;
          const currentStanding = standings.find((s: any) => s.userId === this.currentUser.id);
          const opponentStanding = standings.find((s: any) => s.userId === opponent.userId);
          currentWL = { wins: currentStanding?.wins ?? 0, losses: currentStanding?.losses ?? 0, gamesPlayed: 0 };
          opponentWL = { wins: opponentStanding?.wins ?? 0, losses: opponentStanding?.losses ?? 0, gamesPlayed: 0 };
          hideWL = false; // Show tournament-specific W-L
        } else if (tournamentType === 'bracket') {
          // For bracket: hide W-L (PPR only)
          hideWL = true;
        }
      } catch (e) {
        console.error('[LobbyManager.startDirectMatch] Failed to load tournament data for match intro', e);
      }
    } else if (!opts?.isTournament && opponent.userId !== 'ai_robo_roller') {
      // For non-tournament games: calculate overall W-L
      try {
        if (this.repo.calculateWinLoss) {
          currentWL = await this.repo.calculateWinLoss(this.currentUser.id, { excludeTournaments: true, excludeAI: true });
          opponentWL = await this.repo.calculateWinLoss(opponent.userId, { excludeTournaments: true, excludeAI: true });
        }
      } catch (e) {
        console.error('[LobbyManager.startDirectMatch] Failed to calculate W-L for match intro', e);
      }
    }

    const matchFoundMsg: MatchFoundMessage = {
      kind: 'match_found',
      matchId,
      from: {
        userId: this.currentUser.id,
        userName: this.currentUser.userName,
        wins: currentWL.wins,
        losses: currentWL.losses,
        avgPPR: this.currentUser.avgPPR
      },
      to: {
        userId: opponent.userId,
        userName: opponent.userName
      },
      startingPlayer,
      isTournament: opts?.isTournament
    };

    // Store match locally; opponent will also store on broadcast receive
    this.matches[matchId] = matchFoundMsg;

    const currentPlayer: PresenceMeta = {
      userId: this.currentUser.id,
      userName: this.currentUser.userName,
      mode: 'waiting',
      wins: currentWL.wins,
      losses: currentWL.losses,
      avgPPR: this.currentUser.avgPPR
    };

    const isStarting = startingPlayer === this.currentUser.id;

    // Create opponent object with calculated W-L
    const opponentWithWL = { ...opponent, wins: opponentWL.wins, losses: opponentWL.losses };

    // Send a broadcast so the opponent receives the same startingPlayer + matchId
    // Only the lexicographically smaller userId will broadcast to avoid duplicates
    const shouldBroadcast = this.currentUser.id < opponent.userId;
    if (shouldBroadcast) {
      this.lobby.send({ type: 'broadcast', event: 'lobby', payload: matchFoundMsg });
    }

    // Show intro only on user click path (initiator)
    // Mark that we've shown the match intro to this player
    this.shownMatchIntros.add(matchId);
    this.handlers.showMatchIntroduction?.(currentPlayer, opponentWithWL as any, isStarting, matchId, () => {
      if (!this.playerReadyState[matchId]) {
        this.playerReadyState[matchId] = {};
      }
      this.playerReadyState[matchId][this.currentUser.id] = true;

      // Broadcast readiness to lobby channel so opponent (if listening) can proceed
      this.lobby.send({
        type: 'broadcast',
        event: 'lobby',
        payload: <PlayerReady>{
          kind: 'player_ready',
          matchId: matchId,
          from: { userId: this.currentUser.id, userName: this.currentUser.userName },
          to: { userId: opponent.userId, userName: opponent.userName },
        }
      });

      if (this.playerReadyState[matchId][opponent.userId]) {
        if (isStarting) {
          this.isGameHost = false;
          this.handlers.startMatch?.(matchId,
              { userId: this.currentUser.id, userName: this.currentUser.userName },
              { userId: opponent.userId, userName: opponent.userName }
          );
        } else {
          this.isGameHost = true;
          this.handlers.startMatch?.(matchId,
              { userId: opponent.userId, userName: opponent.userName },
              { userId: this.currentUser.id, userName: this.currentUser.userName }
          );
        }
      }
    }, () => {
      // User clicked cancel
      this.lobby.send({ type: 'broadcast', event: 'lobby', payload: <MatchCancelled>{ kind: 'match_cancelled', matchId: matchId, from: { userId: this.currentUser.id, userName: this.currentUser.userName }, to: { userId: opponent.userId, userName: opponent.userName }, isTournament: opts?.isTournament } });
      delete this.playerReadyState[matchId];
      delete this.matches[matchId];
      this.shownMatchIntros.delete(matchId);
      // For tournaments: just close modal (no return to search)
    }, opts?.titleText, hideWL);
  }

  // Tournament flow helper: require user to click the bracket match before showing intro.
  async registerTournamentClick(opponent: PresenceMeta, opts: { matchId: string; titleText?: string; startingPlayerId?: string; isTournament?: boolean; tournamentId?: string }) {
    const existing = this.matches[opts.matchId];
    if (existing) {
      // Opponent already broadcasted match_found; just show intro using their startingPlayer
      const isStarting = existing.startingPlayer === this.currentUser.id;

      // Determine tournament type and load appropriate W-L records
      let hideWL = false; // Default: show W-L
      let currentWL = { wins: 0, losses: 0, gamesPlayed: 0 };
      let opponentWL = { wins: 0, losses: 0, gamesPlayed: 0 };

      if (opts?.tournamentId && (this.repo as any).sb?.getTournamentById) {
        try {
          const tournament = await (this.repo as any).sb.getTournamentById(opts.tournamentId);
          const tournamentType = tournament?.tournament_type as 'bracket' | 'round_robin' | undefined;

          if (tournamentType === 'round_robin' && tournament.round_robin_data?.standings) {
            // For round robin: load tournament-specific W-L from standings
            const standings = tournament.round_robin_data.standings;
            const currentStanding = standings.find((s: any) => s.userId === this.currentUser.id);
            const opponentStanding = standings.find((s: any) => s.userId === opponent.userId);
            currentWL = { wins: currentStanding?.wins ?? 0, losses: currentStanding?.losses ?? 0, gamesPlayed: 0 };
            opponentWL = { wins: opponentStanding?.wins ?? 0, losses: opponentStanding?.losses ?? 0, gamesPlayed: 0 };
            hideWL = false; // Show tournament-specific W-L
          } else if (tournamentType === 'bracket') {
            // For bracket: hide W-L (PPR only)
            hideWL = true;
          }
        } catch (e) {
          console.error('Failed to load tournament data for match intro', e);
        }
      } else if (!opts?.isTournament) {
        // For non-tournament games: calculate overall W-L
        try {
          if (this.repo.calculateWinLoss) {
            currentWL = await this.repo.calculateWinLoss(this.currentUser.id, { excludeTournaments: true, excludeAI: true });
            opponentWL = await this.repo.calculateWinLoss(opponent.userId, { excludeTournaments: true, excludeAI: true });
          }
        } catch (e) {
          console.error('Failed to calculate W-L for match intro', e);
        }
      }

      const currentPlayer: PresenceMeta = {
        userId: this.currentUser.id,
        userName: this.currentUser.userName,
        mode: 'waiting', wins: currentWL.wins, losses: currentWL.losses, avgPPR: this.currentUser.avgPPR,
        profile_image_url: this.currentUser.profile_image_url
      };
      const opponentWithWL = { ...opponent, wins: opponentWL.wins, losses: opponentWL.losses };
      this.handlers.onMatchFound?.(opponentWithWL, existing.matchId);
      // Mark that we've shown the match intro to this player
      this.shownMatchIntros.add(existing.matchId);
      this.handlers.showMatchIntroduction?.(currentPlayer, opponentWithWL, isStarting, existing.matchId, () => {
        if (!this.playerReadyState[existing.matchId]) this.playerReadyState[existing.matchId] = {};
        this.playerReadyState[existing.matchId][this.currentUser.id] = true;
        this.lobby.send({ type: 'broadcast', event: 'lobby', payload: <PlayerReady>{ kind: 'player_ready', matchId: existing.matchId, from: { userId: this.currentUser.id, userName: this.currentUser.userName }, to: opponent } });
        if (this.playerReadyState[existing.matchId][opponent.userId]) {
          if (isStarting) {
            this.isGameHost = false;
            this.handlers.startMatch?.(existing.matchId, { userId: this.currentUser.id, userName: this.currentUser.userName }, { userId: opponent.userId, userName: opponent.userName });
          } else {
            this.isGameHost = true;
            this.handlers.startMatch?.(existing.matchId, { userId: opponent.userId, userName: opponent.userName }, { userId: this.currentUser.id, userName: this.currentUser.userName });
          }
        }
      }, () => {
        // User clicked cancel
        this.lobby.send({ type: 'broadcast', event: 'lobby', payload: <MatchCancelled>{ kind: 'match_cancelled', matchId: existing.matchId, from: { userId: this.currentUser.id, userName: this.currentUser.userName }, to: opponent, isTournament: opts?.isTournament } });
        delete this.playerReadyState[existing.matchId];
        delete this.matches[existing.matchId];
        this.shownMatchIntros.delete(existing.matchId);
        // For tournaments: just close modal (no return to search)
      }, opts?.titleText, hideWL);
      return;
    }
    // Not broadcast yet — become initiator
    await this.startDirectMatch(opponent, { matchId: opts.matchId, titleText: opts.titleText, startingPlayerId: opts.startingPlayerId, isTournament: opts.isTournament, tournamentId: opts.tournamentId });
  }

}
